import {useTranslation} from 'react-i18next';
import useOpenState from '../../../../hooks/useOpenState';
import React, {useEffect, useRef, useState} from 'react';
import Notes from '../../../../util/notes';
import Grid from '@mui/material/Grid';
import InformationBox from '../../../../components/other/informationBox';
import {Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip} from '@mui/material';
import * as _ from 'lodash';
import Typography from '@mui/material/Typography';
import moment from 'moment/moment';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import NonContactObsFormDialog from './nonContactObsFormDialog';
import {getTableScrollbar} from '../../../../util/scrollbar';
import MessageBox from '../../../../util/messageBox';
import {usePlansStyles} from './plansStyle';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';

const obsColors = {
    null: '#F7A202',
    yes: '#4EAC6F',
    no: '#E25B61',
};

export const NonContactObsForm = (props) => {
    const classes = usePlansStyles();
    const {t} = useTranslation();

    const [isOpenObsForm, openObsForm, closeObsForm] = useOpenState(false, true);
    const [isOpenDeleteEntry, openDeleteEntry, closeDeleteEntry] = useOpenState(
        false,
        true,
    );

    const [obsData, setObsData] = useState(props.patient?.studies?.nonContactObs || []);
    const [keysList, setKeysList] = useState([]);
    const [idToDelete, setIdToDelete] = useState(null);

    // Scroll to end of table
    const scrollRef = useRef(null);

    useEffect(() => {
        if (props.onCustomButtons) {
            props.onCustomButtons([
                <AgyleButton
                    key='add'
                    onClick={openObsForm}
                    colour="primary"
                >
                    {t('Add New Observation')}
                </AgyleButton>
            ]);
        }
        if (props.onHideSubmit) {
            props.onHideSubmit(true);
        }
        if (props.onDisableSubmit) {
            props.onDisableSubmit(props.disableSave);
        }
    }, [props.disableSave]);

    useEffect(() => {
        if (obsData.length > 0 && !keysList.length) {
            const keys = Object.keys(obsData[0]);
            setKeysList(keys);
        }

        if (scrollRef.current) {
            scrollRef.current.scrollIntoView({
                behavior: 'smooth',
                inline: 'end',
            });
        }
    }, [obsData, keysList, scrollRef.current]);

    const handleCloseNonContact = () => {
        props.onClose();
    };

    const handleDeleteEntry = () => {
        const deletedEntry = obsData.find((item) => item.id === idToDelete);
        const newState = [...obsData].filter((item) => item.id !== idToDelete);

        setObsData(newState);

        const data = [
            {
                name: 'studies.nonContactObs',
                value: newState,
                noteToAdd: Notes.getNonContactObsNote(deletedEntry, true),
            },
        ];

        props.onChange(data);

        closeDeleteEntry();
        setIdToDelete(null);
    };

    const renderNoObsInfo = () => {
        return (
            <Grid item xs={12} sx={{px: 2, mt: 2, mb: 4}}>
                <InformationBox type={'info'}>
                    {t('Observations not made.')}
                </InformationBox>
            </Grid>
        );
    };

    const getShortDescription = (key) => {
        switch (key) {
            case 'yes':
                return 'NAD';
            case 'no':
                return 'Abnormality';
            case null:
                return 'NR';
            default:
                return '';
        }
    };

    const renderObsTable = () => {
        return (
            <Grid item xs={12} sx={{px: 2, my: 1}}>
                <TableContainer className={getTableScrollbar()}>
                    <Table aria-label="simple table" ref={scrollRef}>
                        <TableHead style={{display: 'flex'}}>
                            <TableCell sx={{width: '100px', height: '100px'}}></TableCell>
                            {_.map(obsData, (property, key) => {
                                return (
                                    <TableCell
                                        key={property.id}
                                        sx={{'&:last-child td, &:last-child th': {border: 0}}}
                                        style={{
                                            width: '170px',
                                            display: 'inline-block',
                                            padding: 8,
                                        }}
                                    >
                                        <Stack
                                            direction="column"
                                            alignItems="left"
                                            style={{
                                                borderRadius: '10px',
                                                backgroundColor: 'grey',
                                                position: 'relative',
                                            }}
                                            sx={{p: 1}}
                                        >
                                            <Typography variant="body1" component="div">
                                                {moment(property.date).format('DD/MM/YYYY')}
                                            </Typography>
                                            <Typography variant="body1" component="div">
                                                {moment(property.date).format('HH:mm')}
                                            </Typography>
                                            <IconButton
                                                onClick={() => {
                                                    setIdToDelete(property.id);
                                                    openDeleteEntry();
                                                }}
                                                disabled={props.disableSave}
                                                style={{
                                                    position: 'absolute',
                                                    top: '10px',
                                                    right: '0',
                                                }}
                                            >
                                                <DeleteIcon/>
                                            </IconButton>
                                        </Stack>
                                    </TableCell>
                                );
                            })}
                        </TableHead>

                        <TableBody className={classes.columnRow}>
                            <TableCell spacing={2} style={{minWidth: '100px', padding: 8}}>
                                <TableRow
                                    align="right"
                                    sx={{my: 1}}
                                    className={classes.letterRowStaticColor}
                                >
                                    <Typography
                                        variant="h6"
                                        component="div"
                                        sx={{px: 2, py: 0.5}}
                                    >
                                        {t('A')}
                                    </Typography>
                                </TableRow>
                                <TableRow
                                    align="right"
                                    sx={{my: 1}}
                                    className={classes.letterRowStaticColor}
                                >
                                    <Typography
                                        variant="h6"
                                        component="div"
                                        sx={{px: 2, py: 0.5}}
                                    >
                                        {t('B')}
                                    </Typography>
                                </TableRow>
                                <TableRow
                                    align="right"
                                    sx={{my: 1}}
                                    className={classes.letterRowStaticColor}
                                >
                                    <Typography
                                        variant="h6"
                                        component="div"
                                        sx={{px: 2, py: 0.5}}
                                    >
                                        {t('C')}
                                    </Typography>
                                </TableRow>
                                <TableRow
                                    align="right"
                                    sx={{my: 1}}
                                    className={classes.letterRowStaticColor}
                                >
                                    <Typography
                                        variant="h6"
                                        component="div"
                                        sx={{px: 2, py: 0.5}}
                                    >
                                        {t('D')}
                                    </Typography>
                                </TableRow>
                                <TableRow
                                    align="right"
                                    sx={{my: 1}}
                                    className={classes.letterRowStaticColor}
                                >
                                    <Typography
                                        variant="h6"
                                        component="div"
                                        sx={{px: 2, py: 0.5}}
                                    >
                                        {t('E')}
                                    </Typography>
                                </TableRow>
                            </TableCell>
                            {_.map(obsData, (property, key) => {
                                const getComment = (prop) => {
                                    return `${
                                        prop.comment
                                            ? `Comment: ${prop.comment}`
                                            : 'No comment for this observation'
                                    }`;
                                };

                                return (
                                    <TableCell
                                        key={property.id}
                                        sx={{
                                            '&:last-child td, &:last-child th': {border: 0},
                                        }}
                                        style={{
                                            width: '170px',
                                            display: 'inline-block',
                                            padding: 8,
                                        }}
                                    >
                                        <TableRow
                                            align="right"
                                            sx={{my: 1, minHeight: '40px'}}
                                            className={classes.letterRow}
                                            style={{
                                                backgroundColor: obsColors[property.airway?.value],
                                            }}
                                        >
                                            <Tooltip title={getComment(property.airway)}>
                                                <Typography
                                                    variant="body1"
                                                    component="div"
                                                    className={classes.nonContactCellTypography}
                                                    sx={{px: 2, py: 0.5}}
                                                    color="black"
                                                >
                                                    {getShortDescription(property.airway?.value)}
                                                </Typography>
                                            </Tooltip>
                                        </TableRow>
                                        <TableRow
                                            align="right"
                                            sx={{my: 1, minHeight: '40px'}}
                                            className={classes.letterRow}
                                            style={{
                                                backgroundColor: obsColors[property.breathing?.value],
                                            }}
                                        >
                                            <Tooltip title={getComment(property.breathing)}>
                                                <Typography
                                                    variant="body1"
                                                    component="div"
                                                    sx={{px: 2, py: 0.5}}
                                                    color="black"
                                                    className={classes.nonContactCellTypography}
                                                >
                                                    {getShortDescription(property.breathing?.value)}
                                                </Typography>
                                            </Tooltip>
                                        </TableRow>
                                        <TableRow
                                            align="right"
                                            sx={{my: 1, minHeight: '40px'}}
                                            className={classes.letterRow}
                                            style={{
                                                backgroundColor: obsColors[property.circulation?.value],
                                            }}
                                        >
                                            <Tooltip title={getComment(property.circulation)}>
                                                <Typography
                                                    variant="body1"
                                                    component="div"
                                                    sx={{px: 2, py: 0.5}}
                                                    color="black"
                                                    className={classes.nonContactCellTypography}
                                                >
                                                    {getShortDescription(property.circulation?.value)}
                                                </Typography>
                                            </Tooltip>
                                        </TableRow>
                                        <TableRow
                                            align="right"
                                            sx={{my: 1, minHeight: '40px'}}
                                            className={classes.letterRow}
                                            style={{
                                                backgroundColor: obsColors[property.disability?.value],
                                            }}
                                        >
                                            <Tooltip title={getComment(property.disability)}>
                                                <Typography
                                                    variant="body1"
                                                    component="div"
                                                    sx={{px: 2, py: 0.5}}
                                                    color="black"
                                                    className={classes.nonContactCellTypography}
                                                >
                                                    {getShortDescription(property.disability?.value)}
                                                </Typography>
                                            </Tooltip>
                                        </TableRow>
                                        <TableRow
                                            align="right"
                                            sx={{my: 1, minHeight: '40px'}}
                                            className={classes.letterRow}
                                            style={{
                                                backgroundColor: obsColors[property.exposure?.value],
                                            }}
                                        >
                                            <Tooltip title={getComment(property.exposure)}>
                                                <Typography
                                                    variant="body1"
                                                    component="div"
                                                    sx={{px: 2, py: 0.5}}
                                                    color="black"
                                                    className={classes.nonContactCellTypography}
                                                >
                                                    {getShortDescription(property.exposure?.value)}
                                                </Typography>
                                            </Tooltip>
                                        </TableRow>
                                    </TableCell>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>
        );
    };

    return (
        <>
            {_.isEmpty(obsData) && renderNoObsInfo()}
            {!_.isEmpty(obsData) && renderObsTable()}

            {isOpenObsForm &&
                <NonContactObsFormDialog
                    {...props}
                    open={isOpenObsForm}
                    onClose={closeObsForm}
                    obsData={obsData}
                    setObsData={setObsData}
                    handleCloseNonContact={handleCloseNonContact}
                />
            }

            <MessageBox
                open={isOpenDeleteEntry}
                onClose={closeDeleteEntry}
                title={t('Delete Observation')}
                haveAccept={{yes: t('Submit'), no: t('Close')}}
                onAccept={handleDeleteEntry}
                message={t('Are you sure you want to delete this observation?')}
                warning
            />
        </>

    );
};

export default NonContactObsForm;
